import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import projectService, { CreateProjectRequest, IProjectDocument } from '../../../services/projectService';
import { toast } from 'react-toastify';
import Label from '../../../components/form/Label';
import Input from '../../../components/form/input/InputField';
import Button from '../../../shared/components/ui/button/Button';
import Select from '../../../shared/components/form/Select';
import projectTypeService, { ProjectType } from '../../../services/projectTypeService';
import { customerService, Customer } from '../../../services/customerService';
import { userService } from '../../../services/userService';
import { locationService } from '../../../services/locationService';
import { Location } from '../../../types/location';
import { Role } from '../../../types/role';
import { User } from '../../../types/user';
import RightDrawer from '../../../shared/components/form/RightDrawer';
import { CustomerForm } from '../../customer';
import LocationForm from '../../location/form/LocationForm';
import { Plus, Search, X, Check, ChevronRight, ChevronLeft, Building2, Users, MapPin, FileText, Save, ExternalLink } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { FileUpload } from 'primereact/fileupload';
import { ProgressBar } from 'primereact/progressbar';
import { fileService, looksLikeImageMediaUrl } from '../../../services/fileService';
import { useResolvedMediaUrl } from '../../../hooks/useResolvedMediaUrl';
import { ResolvedImage } from '../../../components/media/ResolvedImage';
import {
  FileTextIcon,
  PencilIcon,
  TrashIcon,
  UploadIcon,
} from '../../../icons';

// Tab configuration
const TABS = [
  { id: 'basic', label: 'Project Details', icon: Building2, description: 'Basic project information' },
  { id: 'team', label: 'Team & Resources', icon: Users, description: 'Assign team members' },
  { id: 'location', label: 'Location & Docs', icon: MapPin, description: 'Location and documents' },
  { id: 'review', label: 'Review & Save', icon: Save, description: 'Review and submit' },
] as const;

type TabId = typeof TABS[number]['id'];

function ProjectDocumentThumbnail({
  documentFile,
  documentName,
}: {
  documentFile: string;
  documentName: string;
}) {
  const resolved = useResolvedMediaUrl(documentFile);
  const isImage = /\.(jpe?g|png|gif|webp)(\?|$)/i.test(documentFile);
  if (isImage) {
    if (!resolved) {
      return <div className="w-full h-full min-h-[120px] bg-gray-200 animate-pulse" />;
    }
    return <img src={resolved} alt={documentName} className="w-full h-full object-cover" />;
  }
  return (
    <div className="w-16 h-16 rounded-xl bg-indigo-100 flex items-center justify-center">
      <FileTextIcon className="w-8 h-8 text-indigo-600" />
    </div>
  );
}

function ProjectDocumentOpenLink({
  documentFile,
  className,
  children,
}: {
  documentFile: string;
  className?: string;
  children: ReactNode;
}) {
  const resolved = useResolvedMediaUrl(documentFile);
  if (!resolved) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">Loading…</span>
    );
  }
  return (
    <a href={resolved} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </a>
  );
}

type ProjectFormValues = Omit<CreateProjectRequest, 'lastmodifiedDate' | 'dateCreated'> & {
  lastmodifiedDate?: string;
  dateCreated?: string;
  projectHead?: string;
  projectHeadName?: string;
  manager?: string;
  // Resource assignment fields
  assignedSiteEngineers?: string[];
  assignedSiteSupervisors?: string[];
};

export default function ProjectForm({
  onCreated,
  editingProject,
  initialTab,
}: {
  onCreated?: (createdProject?: any) => void;
  editingProject?: any;
  /** When set (e.g. from edit page with ?completeSetup=1), open this tab first to complete remaining info */
  initialTab?: TabId;
}) {
  const [apiError, setApiError] = useState<string | null>(null);
  const [loadingMasters, setLoadingMasters] = useState(false);
  const [projectTypes, setProjectTypes] = useState<ProjectType[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [managers, setManagers] = useState<User[]>([]);
  const [projectHeads, setProjectHeads] = useState<User[]>([]);
  const [siteEngineers, setSiteEngineers] = useState<User[]>([]);
  const [siteSupervisors, setSiteSupervisors] = useState<User[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLocationFormOpen, setIsLocationFormOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [isCustomerDrawerOpen, setIsCustomerDrawerOpen] = useState(false);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [engineerSearchTerm, setEngineerSearchTerm] = useState('');
  const [supervisorSearchTerm, setSupervisorSearchTerm] = useState('');
  const [dateWarnings, setDateWarnings] = useState<{
    startDatePast?: boolean;
    endDateBeforeStart?: boolean;
  }>({});

  // Tab navigation state – when editing with initialTab (e.g. completeSetup), start on that tab
  const [activeTab, setActiveTab] = useState<TabId>(initialTab || 'basic');
  // After first tab is completed we create project as draft and continue editing (so rest of form does PATCH)
  const [createdDraftProject, setCreatedDraftProject] = useState<any>(null);
  const [savingDraft, setSavingDraft] = useState(false);

  const effectiveEditingProject = editingProject || createdDraftProject;

  // When edit page opens with completeSetup=1, open the first incomplete tab
  useEffect(() => {
    if (editingProject && initialTab) setActiveTab(initialTab as TabId);
  }, [editingProject, initialTab]);
  const isEditMode = Boolean(effectiveEditingProject);
  const { user, role: authenticatedRole } = useAuth();

  // Document upload state
  const [attachedDocuments, setAttachedDocuments] = useState<IProjectDocument[]>([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadingFileNames, setUploadingFileNames] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [editingDocIndex, setEditingDocIndex] = useState<number | null>(null);
  const [editDocName, setEditDocName] = useState('');
  const [editDocType, setEditDocType] = useState('');
  const [newDocType, setNewDocType] = useState<IProjectDocument['documentType']>('Manual');
  const [docNameError, setDocNameError] = useState<string>('');

  const currentUserId = useMemo(() => {
    if (!user) return '';
    return (user._id || user.id || '') as string;
  }, [user]);

  const userRoleName = useMemo(() => {
    if (user?.role && typeof user.role === 'object') {
      return user.role.name || '';
    }
    if (typeof user?.role === 'string') {
      return user.role;
    }
    return authenticatedRole || '';
  }, [user, authenticatedRole]);

  const userRoleType = useMemo(() => {
    if (user?.role && typeof user.role === 'object') {
      return user.role.roleType;
    }
    return undefined;
  }, [user]);

  const normalizedRoleName = (userRoleName || '').toLowerCase();
  const isManagerUser = normalizedRoleName.includes('manager');
  const isHeadUser = normalizedRoleName.includes('head');

  const shouldAutoAssignManager = useMemo(() => {
    if (isEditMode) return false;
    if (!currentUserId) return false;
    if (userRoleType === 'requester') return true;
    return isManagerUser;
  }, [isEditMode, currentUserId, userRoleType, isManagerUser]);

  // Helper function to safely parse dates
  const safeParseDate = (dateValue: any): string => {
    if (!dateValue) return new Date().toISOString().slice(0, 10);

    // Handle string dates
    if (typeof dateValue === 'string') {
      // Check if it's already in YYYY-MM-DD format
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
      }

      // Check if it's a valid date string
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        return date.toISOString().slice(0, 10);
      }
    }

    // Handle Date objects
    if (dateValue instanceof Date) {
      if (!isNaN(dateValue.getTime())) {
        return dateValue.toISOString().slice(0, 10);
      }
    }

    // Handle timestamp numbers
    if (typeof dateValue === 'number') {
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        return date.toISOString().slice(0, 10);
      }
    }

    // If all else fails, return today's date
    console.warn('Invalid date value:', dateValue, 'using current date instead');
    return new Date().toISOString().slice(0, 10);
  };

  // Helper function to format date to YYYY-MM-DD
  const formatDateToString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Project Timeline: year must be 4-digit only (min 1900, max 9999)
  const TIMELINE_MIN_YEAR = 1900;
  const TIMELINE_MAX_YEAR = 9999;
  const DATE_ISO_REGEX = /^\d{4}-\d{2}-\d{2}$/;

  const validateProjectTimelineDate = (
    value: string | undefined,
    field: 'start' | 'end'
  ): string | true => {
    const label = field === 'start' ? 'Start date' : 'End date';
    if (!value || typeof value !== 'string') {
      return field === 'start' ? 'Start date is required' : true;
    }
    const trimmed = value.trim();
    if (!trimmed) return field === 'start' ? 'Start date is required' : true;
    if (!DATE_ISO_REGEX.test(trimmed)) {
      return `${label} must be in YYYY-MM-DD format`;
    }
    const year = parseInt(trimmed.slice(0, 4), 10);
    if (Number.isNaN(year)) return `${label}: invalid year`;
    if (year < TIMELINE_MIN_YEAR || year > TIMELINE_MAX_YEAR) {
      return `${label}: year must be between ${TIMELINE_MIN_YEAR} and ${TIMELINE_MAX_YEAR} (4 digits only)`;
    }
    const date = new Date(trimmed);
    if (Number.isNaN(date.getTime())) return `${label}: invalid date`;
    const [y, m, d] = [date.getFullYear(), date.getMonth(), date.getDate()];
    const parsedYear = trimmed.slice(0, 4);
    const parsedMonth = trimmed.slice(5, 7);
    const parsedDay = trimmed.slice(8, 10);
    if (
      String(y) !== parsedYear ||
      String(m + 1).padStart(2, '0') !== parsedMonth ||
      String(d).padStart(2, '0') !== parsedDay
    ) {
      return `${label}: not a valid calendar date`;
    }
    return true;
  };

  // Helper function to check if date is in the past
  const isDateInPast = (dateString: string): boolean => {
    const today = new Date();
    const date = new Date(dateString);
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return date < today;
  };

  // Helper function to validate date ranges
  const validateDateRange = (startDate: string, endDate: string) => {
    const warnings: { startDatePast?: boolean; endDateBeforeStart?: boolean } = {};

    if (startDate && isDateInPast(startDate)) {
      warnings.startDatePast = true;
    }

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      if (end <= start) {
        warnings.endDateBeforeStart = true;
      }
    }

    setDateWarnings(warnings);
    return Object.keys(warnings).length === 0;
  };

  const nowDateTimeDDMMYYYYhhmmAMPM = useMemo(() => {
    return () => {
      const d = new Date();
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      let hours = d.getHours();
      const minutes = String(d.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      if (hours === 0) hours = 12;
      const hh = String(hours).padStart(2, '0');
      return `${dd}/${mm}/${yyyy} ${hh}:${minutes} ${ampm}`;
    };
  }, []);

  // Helper function to safely extract IDs from assigned resources
  const safeExtractIds = (resources: any): string[] => {
    if (!Array.isArray(resources)) return [];
    return resources.map(resource => {
      if (typeof resource === 'string') return resource;
      if (typeof resource === 'object' && resource !== null) {
        return resource._id || resource.id || '';
      }
      return '';
    }).filter(id => id !== '');
  };

  // Helper function to safely render values (prevent objects from being rendered)
  const safeRender = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    if (typeof value === 'object') {
      console.warn('Attempted to render object:', value);
      return '[Object]';
    }
    return String(value);
  };

  // Helper function to safely extract string values
  const safeExtractString = (value: any): string => {
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value !== null) {
      return value._id || value.id || value.name || '';
    }
    return '';
  };

  const defaultValues: ProjectFormValues = {
    projectId: safeExtractString(editingProject?.projectId),
    projectName: safeExtractString(editingProject?.projectName || editingProject?.name),
    startDate: safeParseDate(editingProject?.startDate),
    endDate: editingProject?.endDate ? safeParseDate(editingProject.endDate) : '',
    projectManager: safeExtractString(editingProject?.projectManager || editingProject?.manager),
    manager: safeExtractString(editingProject?.manager),
    projectHead: safeExtractString(editingProject?.projectHead),
    projectHeadName: safeExtractString(editingProject?.projectHeadName),
    projectType: safeExtractString(editingProject?.projectType),
    projectCategory: '', // Will be auto-populated from project type
    customerId: safeExtractString(editingProject?.customerId || editingProject?.customer),
    locationId: safeExtractString(editingProject?.locationId || editingProject?.location),
    projectTypeId: safeExtractString(editingProject?.projectTypeId || editingProject?.projectType),
    location: safeExtractString(editingProject?.location),
    latitude: safeExtractString(editingProject?.latitude),
    longitude: safeExtractString(editingProject?.longitude),
    assignedSiteEngineers: safeExtractIds(editingProject?.assignedResources?.siteEngineers),
    assignedSiteSupervisors: safeExtractIds(editingProject?.assignedResources?.siteSupervisors)
  };

  const {
    register,
    handleSubmit,
    reset,
    watch,
    getValues,
    setFocus,
    setValue,
    formState: { errors, isValid, isSubmitting, isDirty }
  } = useForm<ProjectFormValues>({
    defaultValues,
    mode: 'onChange'
  });

  // Register fields that are controlled via Select or date input
  useEffect(() => {
    register('projectCategory', { required: 'Project category is required' });
    register('projectTypeId', { required: 'Project Type is required' });
    register('customerId', { required: 'Customer is required' });
    register('locationId'); // Optional - users can select existing or create new locations
    register('startDate', {
      required: 'Start date is required',
      validate: (v) => validateProjectTimelineDate(v, 'start'),
    });
    register('endDate', {
      validate: (v) => {
        const result = validateProjectTimelineDate(v, 'end');
        if (result !== true) return result;
        const start = getValues('startDate');
        if (start && v) {
          const startD = new Date(start);
          const endD = new Date(v);
          startD.setHours(0, 0, 0, 0);
          endD.setHours(0, 0, 0, 0);
          if (endD <= startD) return 'End date must be after start date';
        }
        return true;
      },
    });
    register('projectType'); // will be auto-set from projectTypeId
    register('manager', { required: 'Project manager is required' });
    register('projectHead'); // Optional field
    register('projectHeadName'); // Optional field for display
    register('assignedSiteEngineers'); // Optional array of site engineer IDs
    register('assignedSiteSupervisors'); // Optional array of site supervisor IDs
  }, [register]);

  // Load master dropdowns
  useEffect(() => {
    (async () => {
      try {
        setLoadingMasters(true);
        console.log('🔄 Loading master dropdowns...');
        const [pt, cust, roles] = await Promise.all([
          projectTypeService.getAllProjectTypes().then(data => {
            console.log('📋 Project types loaded:', data);
            console.log('📋 Project types count:', data.length);
            console.log('📋 Sample project type:', data[0]);
            return data;
          }),
          customerService.getActiveCustomers({ limit: 100, page: 1 }).then((r) => {
            console.log('🏢 Customers loaded:', r.data);
            return r.data;
          }),
          // Load roles to find Manager role
          (async () => {
            try {
              const roleService = (await import('../../../services/roleService')).roleService;
              const allRoles = await roleService.getRoles({ limit: 100 });

              // Handle paginated response format
              if (allRoles?.data && Array.isArray(allRoles.data)) return allRoles.data;

              console.warn('Unexpected roles response format:', allRoles);
              return [] as Role[];
            } catch (e) {
              console.warn('Failed to load roles:', e);
              return [] as Role[];
            }
          })()
        ]);

        setProjectTypes(pt);
        setCustomers(cust);

        // Find Project Manager role specifically
        console.log('Available roles:', roles.map(r => ({ name: r.name, id: r._id })));

        // Find roles containing "manager" (e.g., "Manager", "Site Manager", "Team Manager", "Project Manager")
        const managerRoles = roles.filter((r: Role) =>
          r.name.toLowerCase().includes('manager') || r.roleType === 'requester'
        );

        // Find roles containing "head" (e.g., "Head", "Project Head", "Team Head")
        const headRoles = roles.filter((r: Role) =>
          r.name.toLowerCase().includes('head')
        );

        // Find roles containing "engineer" (e.g., "Engineer", "Site Engineer", "Field Engineer")
        const engineerRoles = roles.filter((r: Role) =>
          r.name.toLowerCase().includes('engineer')
        );

        // Find roles containing "supervisor" (e.g., "Supervisor", "Site Supervisor", "Field Supervisor")
        const supervisorRoles = roles.filter((r: Role) =>
          r.name.toLowerCase().includes('supervisor')
        );

        if (managerRoles.length > 0) {
          console.log('Found manager roles:', managerRoles.map(r => r.name));

          try {
            // Load users from all manager roles
            const allManagerUsers: User[] = [];

            for (const role of managerRoles) {
              try {
                const response = await userService.getUsers({ limit: 100, role: role._id, status: 'active' });
                if (response?.users && Array.isArray(response.users)) {
                  allManagerUsers.push(...response.users);
                }
              } catch (e) {
                console.warn(`Failed to load users for role ${role.name}:`, e);
              }
            }

            // Remove duplicates based on user ID
            const uniqueManagers = allManagerUsers.filter((user, index, self) =>
              index === self.findIndex(u => (u._id || u.id) === (user._id || user.id))
            );

            console.log('Found managers from all roles:', uniqueManagers);
            setManagers(uniqueManagers);
          } catch (e) {
            console.warn('Failed to load managers:', e);
            setManagers([]);
          }
        } else {
          console.warn('No Manager roles found. Available roles:', roles.map(r => r.name));
          setManagers([]);
        }

        if (headRoles.length > 0) {
          console.log('Found head roles:', headRoles.map(r => r.name));

          try {
            // Load users from all head roles
            const allHeadUsers: User[] = [];

            for (const role of headRoles) {
              try {
                const response = await userService.getUsers({ limit: 100, role: role._id, status: 'active' });
                if (response?.users && Array.isArray(response.users)) {
                  allHeadUsers.push(...response.users);
                }
              } catch (e) {
                console.warn(`Failed to load users for role ${role.name}:`, e);
              }
            }

            // Remove duplicates based on user ID
            const uniqueHeads = allHeadUsers.filter((user, index, self) =>
              index === self.findIndex(u => (u._id || u.id) === (user._id || user.id))
            );

            console.log('Found project heads from all roles:', uniqueHeads);
            setProjectHeads(uniqueHeads);
          } catch (e) {
            console.warn('Failed to load project heads:', e);
            setProjectHeads([]);
          }
        } else {
          console.warn('No Head roles found. Available roles:', roles.map(r => r.name));
          setProjectHeads([]);
        }

        // Load Site Engineers
        if (engineerRoles.length > 0) {
          console.log('Found engineer roles:', engineerRoles.map(r => r.name));

          try {
            // Load users from all engineer roles
            const allEngineerUsers: User[] = [];

            for (const role of engineerRoles) {
              try {
                const response = await userService.getUsers({ limit: 100, role: role._id, status: 'active' });
                if (response?.users && Array.isArray(response.users)) {
                  allEngineerUsers.push(...response.users);
                }
              } catch (e) {
                console.warn(`Failed to load users for role ${role.name}:`, e);
              }
            }

            // Remove duplicates based on user ID
            const uniqueEngineers = allEngineerUsers.filter((user, index, self) =>
              index === self.findIndex(u => (u._id || u.id) === (user._id || user.id))
            );

            console.log('Found site engineers from all roles:', uniqueEngineers);
            setSiteEngineers(uniqueEngineers);
          } catch (e) {
            console.warn('Failed to load site engineers:', e);
            setSiteEngineers([]);
          }
        } else {
          console.warn('No Engineer roles found. Available roles:', roles.map(r => r.name));
          setSiteEngineers([]);
        }

        // Load Site Supervisors
        if (supervisorRoles.length > 0) {
          console.log('Found supervisor roles:', supervisorRoles.map(r => r.name));

          try {
            // Load users from all supervisor roles
            const allSupervisorUsers: User[] = [];

            for (const role of supervisorRoles) {
              try {
                const response = await userService.getUsers({ limit: 100, role: role._id, status: 'active' });
                if (response?.users && Array.isArray(response.users)) {
                  allSupervisorUsers.push(...response.users);
                }
              } catch (e) {
                console.warn(`Failed to load users for role ${role.name}:`, e);
              }
            }

            // Remove duplicates based on user ID
            const uniqueSupervisors = allSupervisorUsers.filter((user, index, self) =>
              index === self.findIndex(u => (u._id || u.id) === (user._id || user.id))
            );

            console.log('Found site supervisors from all roles:', uniqueSupervisors);
            setSiteSupervisors(uniqueSupervisors);
          } catch (e) {
            console.warn('Failed to load site supervisors:', e);
            setSiteSupervisors([]);
          }
        } else {
          console.warn('No Supervisor roles found. Available roles:', roles.map(r => r.name));
          setSiteSupervisors([]);
        }
      } catch (e) {
        console.error('Failed to load masters:', e);
        toast.error('Failed to load form data');
      } finally {
        setLoadingMasters(false);
      }
    })();
  }, []);

  // Load locations
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const response = await locationService.getLocations({
          limit: 100,
          status: 'active',
          page: 1
        });
        const allLocations = response.data || [];
        setLocations(allLocations);
      } catch (error) {
        console.error('Failed to load locations:', error);
      }
    };
    loadLocations();
  }, []);

  // Handle location creation/update
  const handleLocationAdd = async (location: Location) => {
    const newLocationId = String(location._id || location.id);

    // Close the drawer first
    setEditingLocation(null);
    setIsLocationFormOpen(false);

    // Refresh locations list and then select the new location
    try {
      const response = await locationService.getLocations({
        limit: 100,
        status: 'active',
        page: 1
      });
      const fetchedLocations = response.data || [];
      
      // Ensure the new location is in the list even if refresh is slightly behind
      const locationExists = fetchedLocations.some(l => String(l._id || l.id) === newLocationId);
      const updatedLocations = locationExists 
        ? fetchedLocations 
        : [location, ...fetchedLocations];
        
      console.log(`📍 Updated locations list. Count: ${updatedLocations.length}. New location exists: ${locationExists}`);
      setLocations(updatedLocations);

      // Auto-select the newly created/updated location after the list is updated
      if (newLocationId) {
        // Small delay to ensure state is updated and Select component re-renders
        setTimeout(() => {
          console.log(`🎯 Auto-selecting location: ${newLocationId}`);
          setValue('locationId', newLocationId, { shouldDirty: true, shouldValidate: true });
          setValue('latitude', location.latitude || '', { shouldDirty: true, shouldValidate: true });
          setValue('longitude', location.longitude || '', { shouldDirty: true, shouldValidate: true });
          toast.success(`Location "${location.name}" selected`);
        }, 150);
      }
    } catch (error) {
      console.error('Failed to refresh locations:', error);
      // Even if refresh fails, try to select the location
      if (newLocationId) {
        setValue('locationId', newLocationId, { shouldDirty: true, shouldValidate: true });
      }
    }
  };

  // Load existing documents when editing
  useEffect(() => {
    if (editingProject?.attachedDocuments) {
      setAttachedDocuments(editingProject.attachedDocuments || []);
    } else {
      setAttachedDocuments([]);
    }
  }, [editingProject]);

  // Upload file directly to S3 via a presigned PUT URL and return the stored URL.
  async function uploadFileToS3(file: File): Promise<string> {
    try {
      const uploaded = await fileService.uploadFile(file);
      return uploaded.url;
    } catch (error: any) {
      console.error('File upload error:', error);
      throw new Error(error?.message || 'Failed to upload file');
    }
  }

  const MAX_FILE_SIZE_MB = 10;
  const ACCEPTED_EXTENSIONS = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.txt';

  // Direct upload: when user selects or drops files, upload immediately (no extra button)
  const handleFilesChosen = async (files: File[]) => {
    if (!files.length) return;
    const valid: File[] = [];
    for (const f of files) {
      if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        toast.error(`${f.name} exceeds ${MAX_FILE_SIZE_MB}MB`);
        continue;
      }
      valid.push(f);
    }
    if (!valid.length) return;
    setDocNameError('');
    setUploadingFile(true);
    setUploadingFileNames(valid.map((f) => f.name));
    const newDocs: IProjectDocument[] = [];
    try {
      for (let i = 0; i < valid.length; i++) {
        const file = valid[i];
        try {
          const fileUrl = await uploadFileToS3(file);
          const baseName = file.name.replace(/\.[^/.]+$/, '');
          const docName = valid.length === 1 ? baseName : `${baseName} (${i + 1})`;
          const newDoc: IProjectDocument = {
            documentName: docName,
            documentType: newDocType,
            documentFile: fileUrl,
          };
          newDocs.push(newDoc);
          setAttachedDocuments((prev) => [...prev, newDoc]);
          toast.success(`Uploaded ${file.name}`);
        } catch (err) {
          console.error(`Failed to upload ${file.name}`, err);
          toast.error(`Failed to upload ${file.name}`);
        }
      }
    } finally {
      setUploadingFile(false);
      setUploadingFileNames([]);
    }
  };

  const openFileInput = () => {
    if (uploadingFile) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = ACCEPTED_EXTENSIONS;
    input.multiple = true;
    input.onchange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      const files = Array.from(target.files || []) as File[];
      if (files.length) handleFilesChosen(files);
      input.value = '';
    };
    input.click();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (uploadingFile) return;
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) handleFilesChosen(files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploadingFile) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  // Legacy handler for PrimeReact FileUpload (for backward compatibility)
  const customUploadHandler = async (event: any) => {
    const files = event.files;

    if (!files || files.length === 0) return;

    setUploadingFile(true);

    try {
      const newDocs: IProjectDocument[] = [];

      for (const file of files) {
        try {
          // Upload to S3
          const fileUrl = await uploadFileToS3(file);

          // Create document object
          const newDoc: IProjectDocument = {
            documentName: file.name, // Default name is filename
            documentType: 'Manual',  // Default type
            documentFile: fileUrl,
          };
          newDocs.push(newDoc);

          toast.success(`Uploaded ${file.name}`);
        } catch (err) {
          console.error(`Failed to upload ${file.name}`, err);
          toast.error(`Failed to upload ${file.name}`);
        }
      }

      if (newDocs.length > 0) {
        setAttachedDocuments((prev) => [...prev, ...newDocs]);
      }

      // Clear the file upload component
      event.options.clear();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error processing files');
    } finally {
      setUploadingFile(false);
    }
  };

  const startEditingDoc = (index: number, doc: IProjectDocument) => {
    setEditingDocIndex(index);
    setEditDocName(doc.documentName);
    setEditDocType(doc.documentType);
  };

  const saveEditingDoc = () => {
    if (editingDocIndex === null) return;

    const newDocs = [...attachedDocuments];
    if (newDocs[editingDocIndex]) {
      newDocs[editingDocIndex] = {
        ...newDocs[editingDocIndex],
        documentName: editDocName,
        documentType: editDocType as IProjectDocument['documentType']
      };
      setAttachedDocuments(newDocs);
    }

    setEditingDocIndex(null);
    setEditDocName('');
    setEditDocType('');
  };

  const cancelEditingDoc = () => {
    setEditingDocIndex(null);
    setEditDocName('');
    setEditDocType('');
  };

  const removeDoc = (index: number) => {
    const newDocs = [...attachedDocuments];
    newDocs.splice(index, 1);
    setAttachedDocuments(newDocs);

    // If we were editing this one, cancel edit
    if (editingDocIndex === index) {
      cancelEditingDoc();
    } else if (editingDocIndex !== null && editingDocIndex > index) {
      // Adjust index if we removed an item before the one being edited
      setEditingDocIndex(editingDocIndex - 1);
    }
  };

  // Set form values when editing project changes
  useEffect(() => {
    if (editingProject && projectTypes.length > 0 && customers.length > 0) {
      // Debug: Log the editing project data to help identify issues
      console.log('Editing project data:', editingProject);
      console.log('Available project types:', projectTypes);
      console.log('Available customers:', customers);

      // If we have a projectTypeId, try to auto-set the category
      let projectCategory = editingProject.projectCategory || '';
      if (editingProject.projectTypeId && projectTypes.length > 0) {
        const projectType = projectTypes.find(pt => pt._id === editingProject.projectTypeId);
        if (projectType) {
          projectCategory = projectType.category;
        }
      }

      // Find the correct manager ID from the managers list
      let managerId = '';
      if (editingProject.manager) {
        // If we have a manager object or ID, extract the ID safely
        if (typeof editingProject.manager === 'string') {
          managerId = editingProject.manager;
        } else if (typeof editingProject.manager === 'object' && editingProject.manager !== null) {
          managerId = editingProject.manager._id || editingProject.manager.id || '';
        }
      } else if (editingProject.projectManager) {
        // If we have a manager name, try to find the ID
        const managerName = editingProject.projectManager;
        const foundManager = managers.find(m => m.name === managerName);
        if (foundManager) {
          managerId = (foundManager._id || foundManager.id) as string;
        }
      }

      // Find the correct project head ID from the project heads list
      let projectHeadId = '';

      // Check multiple possible locations for project head data
      const projectHeadData = editingProject.projectHead ||
        editingProject.assignedResources?.projectHead ||
        editingProject.assignedResources?.projectHeadId;

      if (projectHeadData) {
        // If we have a project head object or ID, extract the ID safely
        if (typeof projectHeadData === 'string') {
          projectHeadId = projectHeadData;
        } else if (typeof projectHeadData === 'object' && projectHeadData !== null) {
          projectHeadId = projectHeadData._id || projectHeadData.id || '';
        }
      } else if (editingProject.projectHeadName) {
        // If we have a project head name, try to find the ID
        const headName = editingProject.projectHeadName;
        const foundHead = projectHeads.find(h => h.name === headName);
        if (foundHead) {
          projectHeadId = (foundHead._id || foundHead.id) as string;
        }
      }

      console.log('Setting form values:', {
        projectTypeId: editingProject.projectTypeId,
        customerId: editingProject.customerId,
        managerId,
        projectHeadId,
        assignedResources: editingProject?.assignedResources,
        assignedSiteEngineers: editingProject?.assignedResources?.siteEngineers,
        assignedSiteSupervisors: editingProject?.assignedResources?.siteSupervisors
      });

      // Debug project head data specifically
      console.log('Project head debug info:', {
        'editingProject.projectHead': editingProject.projectHead,
        'editingProject.projectHeadName': editingProject.projectHeadName,
        'editingProject.assignedResources': editingProject.assignedResources,
        'editingProject.assignedResources?.projectHead': editingProject.assignedResources?.projectHead,
        'editingProject.assignedResources?.projectHeadId': editingProject.assignedResources?.projectHeadId,
        'projectHeadData': projectHeadData,
        'projectHeadId': projectHeadId
      });

      reset({
        projectId: safeExtractString(editingProject.projectId),
        projectName: safeExtractString(editingProject.projectName || editingProject.name),
        startDate: safeParseDate(editingProject.startDate),
        endDate: editingProject.endDate ? safeParseDate(editingProject.endDate) : '',
        projectManager: safeExtractString(editingProject.projectManager || editingProject.manager),
        manager: managerId, // Set the manager ID for the select
        projectHead: projectHeadId, // Set the project head ID for the select
        projectHeadName: safeExtractString(editingProject.projectHeadName),
        projectType: safeExtractString(editingProject.projectType),
        projectCategory: projectCategory,
        customerId: safeExtractString(editingProject.customerId || editingProject.customer),
        locationId: safeExtractString(editingProject.locationId || editingProject.location),
        projectTypeId: safeExtractString(editingProject.projectTypeId || editingProject.projectType),
        location: safeExtractString(editingProject.location),
        latitude: safeExtractString(editingProject.latitude),
        longitude: safeExtractString(editingProject.longitude),
        assignedSiteEngineers: safeExtractIds(editingProject?.assignedResources?.siteEngineers),
        assignedSiteSupervisors: safeExtractIds(editingProject?.assignedResources?.siteSupervisors)
      });
    }
  }, [editingProject, reset, projectTypes, customers, managers, projectHeads]);

  // Fallback effect to set basic form values even if masters aren't loaded yet
  useEffect(() => {
    if (editingProject && !loadingMasters) {
      // Extract manager ID safely
      let managerId = '';
      if (editingProject.manager) {
        if (typeof editingProject.manager === 'string') {
          managerId = editingProject.manager;
        } else if (typeof editingProject.manager === 'object' && editingProject.manager !== null) {
          managerId = editingProject.manager._id || editingProject.manager.id || '';
        }
      }

      // Extract project head ID safely
      let projectHeadId = '';

      // Check multiple possible locations for project head data
      const projectHeadData = editingProject.projectHead ||
        editingProject.assignedResources?.projectHead ||
        editingProject.assignedResources?.projectHeadId;

      if (projectHeadData) {
        if (typeof projectHeadData === 'string') {
          projectHeadId = projectHeadData;
        } else if (typeof projectHeadData === 'object' && projectHeadData !== null) {
          projectHeadId = projectHeadData._id || projectHeadData.id || '';
        }
      }

      // Set basic values that don't depend on master data
      setValue('projectId', safeExtractString(editingProject.projectId));
      setValue('projectName', safeExtractString(editingProject.projectName || editingProject.name));
      setValue('startDate', safeParseDate(editingProject.startDate));
      setValue('endDate', editingProject.endDate ? safeParseDate(editingProject.endDate) : '');
      setValue('projectManager', safeExtractString(editingProject.projectManager || editingProject.manager));
      setValue('manager', managerId); // Set the manager ID for the select
      setValue('projectHead', projectHeadId); // Set the project head ID for the select
      setValue('projectHeadName', safeExtractString(editingProject.projectHeadName));
      setValue('projectType', safeExtractString(editingProject.projectType));
      setValue('customerId', safeExtractString(editingProject.customerId || editingProject.customer));
      setValue('locationId', safeExtractString(editingProject.locationId || editingProject.location));
      setValue('projectTypeId', safeExtractString(editingProject.projectTypeId || editingProject.projectType));
      setValue('location', safeExtractString(editingProject.location));
      setValue('latitude', safeExtractString(editingProject.latitude));
      setValue('longitude', safeExtractString(editingProject.longitude));
      setValue('assignedSiteEngineers', safeExtractIds(editingProject?.assignedResources?.siteEngineers));
      setValue('assignedSiteSupervisors', safeExtractIds(editingProject?.assignedResources?.siteSupervisors));

    }
  }, [editingProject, loadingMasters, setValue]);

  // Handle resource assignments when site engineers and supervisors are loaded
  useEffect(() => {
    if (editingProject && siteEngineers.length > 0 && siteSupervisors.length > 0) {
      console.log('Setting resource assignments:', {
        assignedSiteEngineers: editingProject?.assignedResources?.siteEngineers,
        assignedSiteSupervisors: editingProject?.assignedResources?.siteSupervisors,
        availableEngineers: siteEngineers.length,
        availableSupervisors: siteSupervisors.length
      });

      // Set resource assignments
      setValue('assignedSiteEngineers', safeExtractIds(editingProject?.assignedResources?.siteEngineers));
      setValue('assignedSiteSupervisors', safeExtractIds(editingProject?.assignedResources?.siteSupervisors));
    }
  }, [editingProject, siteEngineers, siteSupervisors, setValue]);

  // Watch fields for success states
  const watched = watch();

  // Debug: Log watched values to identify any objects being rendered
  useEffect(() => {
    if (isEditMode) {
      console.log('Watched values:', watched);
      // Check for any object values that might cause rendering issues
      Object.entries(watched).forEach(([key, value]) => {
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          console.warn(`Object found in watched.${key}:`, value);
        }
      });
    }
  }, [watched, isEditMode]);

  // Validate dates when they change
  useEffect(() => {
    if (watched.startDate || watched.endDate) {
      validateDateRange(watched.startDate || '', watched.endDate || '');
    }
  }, [watched.startDate, watched.endDate]);

  // Tab navigation helpers
  const currentTabIndex = TABS.findIndex(t => t.id === activeTab);

  const isTabValid = (tabId: TabId): boolean => {
    switch (tabId) {
      case 'basic':
        // Basic tab: Project ID, Name, Type, Customer, and Start Date are required
        // Manager is in team tab, so don't require it here
        const basicValid = !!(
          watched.projectId?.trim() &&
          watched.projectName?.trim() &&
          watched.projectTypeId &&
          watched.customerId &&
          watched.startDate &&
          !errors.projectId &&
          !errors.projectName &&
          !errors.projectTypeId &&
          !errors.customerId &&
          !errors.startDate
        );
        return basicValid;
      case 'team':
        // Team tab: Project Manager is required
        const teamValid = !!(
          watched.manager &&
          !errors.manager
        );
        return teamValid;
      case 'location':
        // Location tab: All fields are optional, but if provided, they should be valid
        const locationValid = !errors.locationId && !errors.latitude && !errors.longitude;
        return locationValid;
      case 'review':
        // Review tab: Check overall form validity
        return isValid;
      default:
        return true;
    }
  };

  const canProceedToNext = (): boolean => {
    return isTabValid(activeTab) && !uploadingFile;
  };

  const saveDraftAndContinue = async () => {
    const values = getValues();
    const normalizedProjectId = values.projectId.trim().toUpperCase();
    const now = nowDateTimeDDMMYYYYhhmmAMPM();
    const draftPayload: CreateProjectRequest = {
      projectId: normalizedProjectId,
      projectName: values.projectName.trim(),
      startDate: values.startDate || '',
      endDate: values.endDate || '',
      lastmodifiedDate: now,
      dateCreated: now,
      projectManager: currentUserId || values.manager || '',
      projectHead: values.projectHead || '',
      projectType: values.projectType.trim(),
      projectCategory: values.projectCategory.trim(),
      customerId: values.customerId.trim(),
      projectTypeId: values.projectTypeId.trim(),
      name: values.projectName.trim(),
      assignedResources: {
        projectManager: currentUserId || values.manager || '',
        projectHead: values.projectHead || '',
        siteEngineers: values.assignedSiteEngineers || [],
        siteSupervisors: values.assignedSiteSupervisors || [],
      },
    };
    if (values.locationId?.trim()) draftPayload.locationId = values.locationId.trim();
    try {
      setSavingDraft(true);
      setApiError(null);
      const created = await projectService.createProject(draftPayload);
      setCreatedDraftProject(created);
      toast.success('Draft saved. Continue with team and location.');
      setActiveTab(TABS[currentTabIndex + 1].id);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string }; message?: string }; message?: string };
      const msg = err?.response?.data?.message || err?.message || 'Failed to save draft';
      setApiError(msg);
      toast.error(msg);
    } finally {
      setSavingDraft(false);
    }
  };

  const goToNextTab = () => {
    if (currentTabIndex < TABS.length - 1) {
      if (canProceedToNext()) {
        if (activeTab === 'basic' && !editingProject && !createdDraftProject) {
          void saveDraftAndContinue();
          return;
        }
        setActiveTab(TABS[currentTabIndex + 1].id);
      } else {
        // Show specific error messages for missing fields
        const missingFields: string[] = [];
        if (activeTab === 'basic') {
          if (!watched.projectId?.trim()) missingFields.push('Project ID');
          if (!watched.projectName?.trim()) missingFields.push('Project Name');
          if (!watched.projectTypeId) missingFields.push('Project Type');
          if (!watched.customerId) missingFields.push('Customer');
          if (!watched.startDate) missingFields.push('Start Date');
        } else if (activeTab === 'team') {
          if (!watched.manager) missingFields.push('Project Manager');
        }

        if (missingFields.length > 0) {
          toast.warning(`Please complete the following required fields: ${missingFields.join(', ')}`);
        } else {
          toast.warning(`Please complete all required fields in the "${TABS[currentTabIndex].label}" section`);
        }
      }
    }
  };

  const goToPrevTab = () => {
    if (currentTabIndex > 0) {
      setActiveTab(TABS[currentTabIndex - 1].id);
    }
  };

  const goToTab = (tabId: TabId) => {
    // Allow going to previous tabs freely, but validate for forward navigation
    const targetIndex = TABS.findIndex(t => t.id === tabId);
    if (uploadingFile) {
      toast.warning('Please wait for file upload to complete');
      return;
    }
    if (targetIndex <= currentTabIndex) {
      setActiveTab(tabId);
    } else {
      // For forward navigation, check if all previous tabs are valid
      let canProceed = true;
      for (let i = 0; i < targetIndex; i++) {
        if (!isTabValid(TABS[i].id)) {
          canProceed = false;
          toast.warning(`Please complete the "${TABS[i].label}" section first`);
          setActiveTab(TABS[i].id);
          break;
        }
      }
      if (canProceed) {
        setActiveTab(tabId);
      }
    }
  };

  const onSubmit = async (values: ProjectFormValues) => {
    setApiError(null);
    try {
      const normalizedProjectId = values.projectId.trim().toUpperCase();
      const validId = /^[A-Z0-9-]+$/.test(normalizedProjectId);
      if (!validId) {
        const msg = 'Project ID must contain only uppercase letters, numbers, and hyphens';
        setApiError(msg);
        toast.error(msg);
        return;
      }

      // Validate timeline dates: 4-digit year only, valid range
      const startDateCheck = validateProjectTimelineDate(values.startDate, 'start');
      if (startDateCheck !== true) {
        setApiError(startDateCheck);
        toast.error(startDateCheck);
        return;
      }
      if (values.endDate) {
        const endDateCheck = validateProjectTimelineDate(values.endDate, 'end');
        if (endDateCheck !== true) {
          setApiError(endDateCheck);
          toast.error(endDateCheck);
          return;
        }
      }
      // Validate date ranges (end after start)
      if (values.startDate && values.endDate) {
        const isValidRange = validateDateRange(values.startDate, values.endDate);
        if (!isValidRange) {
          if (dateWarnings.endDateBeforeStart) {
            const msg = 'End date must be after start date';
            setApiError(msg);
            toast.error(msg);
            return;
          }
        }
      }

      // Prepare payload, excluding locationId if not selected
      const payload: CreateProjectRequest = {
        ...values,
        projectId: normalizedProjectId,
        projectName: values.projectName.trim(),
        projectManager: values.projectManager?.trim?.() || values.projectManager,
        projectHead: values.projectHead || '', // Send the user ID
        projectType: values.projectType.trim(),
        projectCategory: values.projectCategory.trim(),
        customerId: values.customerId.trim(),
        projectTypeId: values.projectTypeId.trim(),
        lastmodifiedDate: nowDateTimeDDMMYYYYhhmmAMPM(),
        dateCreated: nowDateTimeDDMMYYYYhhmmAMPM(),
        // Assigned Resources
        assignedResources: {
          projectManager: values.manager || '',
          projectHead: values.projectHead || '',
          siteEngineers: values.assignedSiteEngineers || [],
          siteSupervisors: values.assignedSiteSupervisors || []
        },
        name: values.projectName.trim(),
        // Include documents if any
        attachedDocuments: attachedDocuments.length > 0 ? attachedDocuments : undefined,
      };

      // Only include locationId if it's actually selected (not empty)
      if (values.locationId && values.locationId.trim()) {
        payload.locationId = values.locationId.trim();
      }

      if (isEditMode && effectiveEditingProject) {
        // Update existing project (including draft we just created after tab 1)
        const projectId = effectiveEditingProject._id || effectiveEditingProject.id;
        const updatedProject = await projectService.updateProject(projectId, payload);
        toast.success(createdDraftProject ? 'Project created successfully' : 'Project updated successfully');
        onCreated?.(updatedProject);
        return;
      } else {
        // Create new project
        const createdProject = await projectService.createProject(payload);
        toast.success('Project created successfully');

        // Pass the created project to the callback for navigation
        onCreated?.(createdProject);
        return; // Exit early to prevent form reset
      }

      onCreated?.();

      if (!isEditMode) {
        reset({
          ...defaultValues,
          startDate: new Date().toISOString().slice(0, 10),
          projectCategory: '',
          projectHead: '',
          projectHeadName: '',
          assignedSiteEngineers: [],
          assignedSiteSupervisors: []
        });
        setFocus('projectId');
      }
    } catch (e: any) {
      const action = isEditMode ? 'update' : 'create';
      const message = e?.response?.data?.message || e?.message || `Failed to ${action} project`;
      setApiError(message);
      toast.error(message);
    }
  };

  // Helper function to get category display label
  const getCategoryLabel = (category: string) => {
    const option = categoryOptions.find(opt => opt.value === category);
    return option ? option.label : category;
  };

  // Function to refresh customers list
  const refreshCustomers = async (options?: { fallbackCustomer?: Customer; selectCustomerId?: string }) => {
    try {
      setIsLoadingCustomers(true);
      const customersData = await customerService.getActiveCustomers({ limit: 500, page: 1 });
      let refreshedCustomers = customersData.data;

      if (options?.fallbackCustomer) {
        const fallback = options.fallbackCustomer;
        const exists = refreshedCustomers.some((customer) => customer._id === fallback._id);
        if (exists) {
          refreshedCustomers = refreshedCustomers.map((customer) =>
            customer._id === fallback._id ? fallback : customer
          );
        } else {
          refreshedCustomers = [fallback, ...refreshedCustomers];
        }
      }

      setCustomers(refreshedCustomers);

      if (options?.selectCustomerId) {
        setValue('customerId', options.selectCustomerId, { shouldDirty: true, shouldValidate: true });
      }

      return refreshedCustomers;
    } catch (error) {
      console.error('Failed to refresh customers:', error);
      toast.error('Failed to refresh customers list');
      if (options?.fallbackCustomer) {
        setCustomers((prev) => {
          const exists = prev.some((customer) => customer._id === options.fallbackCustomer?._id);
          if (exists) {
            return prev.map((customer) =>
              customer._id === options.fallbackCustomer?._id ? (options.fallbackCustomer as Customer) : customer
            );
          }
          return [options.fallbackCustomer as Customer, ...prev];
        });
        if (options.selectCustomerId) {
          setValue('customerId', options.selectCustomerId, { shouldDirty: true, shouldValidate: true });
        }
      }
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  // Handle customer creation from drawer
  const handleCustomerCreated = async (customerData: any) => {
    try {
      console.log('Customer created with data:', customerData);

      // Create the customer via API
      const createResp = await customerService.createCustomer(customerData);
      const createdCustomer = createResp.data as unknown as Customer;
      console.log('Customer create response:', createResp);

      // Detect "approval required" flow (202 Accepted with approvalRequestId or no _id in data)
      const isApprovalFlow =
        (createResp as any)?.message?.toLowerCase?.().includes('approval') ||
        !(createdCustomer && (createdCustomer as any)?._id);

      if (isApprovalFlow) {
        toast.info(
          'Customer submitted for approval. It will appear here automatically once approved.'
        );
        // Refresh list (may be unchanged yet) and close drawer
        await refreshCustomers();
        setIsCustomerDrawerOpen(false);
        return;
      }

      // Refresh the customers list to include the new customer from backend (with fallback)
      if (createdCustomer && createdCustomer._id) {
        await refreshCustomers({
          fallbackCustomer: createdCustomer,
          selectCustomerId: createdCustomer._id,
        });
        toast.success(`Customer "${customerData.name}" created and selected!`);
      } else {
        await refreshCustomers();
      }

      // Close the drawer
      setIsCustomerDrawerOpen(false);

    } catch (error: any) {
      console.error('Error handling customer creation:', error);
      const msg = error.response?.data?.message || error.message || 'Failed to create customer. Please try again.';
      toast.error(msg);
      throw error;
    }
  };

  // Handle customer form close
  const handleCustomerFormClose = () => {
    setIsCustomerDrawerOpen(false);
    // Refresh customers when closing in case a customer was created
    refreshCustomers();
  };

  // Options mappers
  const categoryOptions = [
    { value: 'wind', label: 'Wind' },
    { value: 'solar', label: 'Solar' },
    { value: 'hybrid', label: 'Hybrid' },
    { value: 'bess', label: 'BESS' },
  ];
  const projectTypeOptions = projectTypes.map((pt) => {
    console.log('🎯 Mapping project type:', pt);
    return { value: pt._id, label: pt.name };
  });
  console.log('🎯 Final project type options:', projectTypeOptions);
  const customerOptions = customers.map((c) => ({ value: c._id, label: c.name }));
  // Filter managers to only show users with manager roles
  const filteredManagers = managers.filter(manager => {
    const roleInfo = manager.role as any;
    const roleName = roleInfo?.name?.toLowerCase?.() || (typeof roleInfo === 'string' ? roleInfo.toLowerCase() : '');
    const roleType = roleInfo?.roleType;
    return roleName.includes('manager') || roleType === 'requester';
  });

  // Filter project heads to only show users with head roles  
  const filteredProjectHeads = projectHeads.filter(head =>
    (head.role as any)?.name?.toLowerCase().includes('head')
  );

  const managerOptions = useMemo(() => {
    const options = filteredManagers.map((u) => ({
      value: (u._id || u.id) as string,
      label: `${u.name} (${u.email})`
    }));

    if (currentUserId && (isManagerUser || userRoleType === 'requester')) {
      const exists = options.some((opt) => opt.value === currentUserId);
      if (!exists && user?.name) {
        const contact = user.email || user.mobile;
        options.push({
          value: currentUserId,
          label: contact ? `${user.name} (${contact})` : user.name
        });
      }
    }

    return options;
  }, [filteredManagers, currentUserId, isManagerUser, userRoleType, user?.name, user?.email, user?.mobile]);

  const projectHeadOptions = useMemo(() => {
    const options = filteredProjectHeads.map((u) => ({
      value: (u._id || u.id) as string,
      label: `${u.name} (${u.email})`
    }));

    if (currentUserId && isHeadUser && user?.name) {
      const exists = options.some((opt) => opt.value === currentUserId);
      if (!exists) {
        const contact = user.email || user.mobile;
        options.push({
          value: currentUserId,
          label: contact ? `${user.name} (${contact})` : user.name
        });
      }
    }

    return options;
  }, [filteredProjectHeads, currentUserId, isHeadUser, user?.name, user?.email, user?.mobile]);

  const managerSelectDisabled = !isEditMode && isManagerUser;
  const projectHeadSelectDisabled = !isEditMode && isHeadUser;
  // Filter engineers and supervisors based on search terms AND role type
  const filteredSiteEngineers = siteEngineers.filter(engineer => {
    const matchesSearch = engineer.name.toLowerCase().includes(engineerSearchTerm.toLowerCase()) ||
      engineer.email.toLowerCase().includes(engineerSearchTerm.toLowerCase());
    const hasEngineerRole = (engineer.role as any)?.name?.toLowerCase().includes('engineer');
    return matchesSearch && hasEngineerRole;
  });

  const filteredSiteSupervisors = siteSupervisors.filter(supervisor => {
    const matchesSearch = supervisor.name.toLowerCase().includes(supervisorSearchTerm.toLowerCase()) ||
      supervisor.email.toLowerCase().includes(supervisorSearchTerm.toLowerCase());
    const hasSupervisorRole = (supervisor.role as any)?.name?.toLowerCase().includes('supervisor');
    return matchesSearch && hasSupervisorRole;
  });

  useEffect(() => {
    if (!shouldAutoAssignManager) return;
    if (!currentUserId) return;
    if (watched.manager) return;
    if (!managers || managers.length === 0) return;

    const currentManager = managers.find(m => (m._id || m.id) === currentUserId);
    if (currentManager) {
      setValue('manager', currentUserId, { shouldDirty: false, shouldValidate: true });
      setValue('projectManager', currentManager.name, { shouldDirty: false, shouldValidate: true });
    } else {
      setValue('manager', currentUserId, { shouldDirty: false, shouldValidate: true });
      if (user?.name) {
        setValue('projectManager', user.name, { shouldDirty: false, shouldValidate: true });
      }
    }
  }, [shouldAutoAssignManager, currentUserId, managers, setValue, watched.manager, user?.name]);

  useEffect(() => {
    if (isEditMode) return;
    if (!isHeadUser) return;
    if (!currentUserId) return;
    if (watched.projectHead) return;

    const currentHead = projectHeads.find(h => (h._id || h.id) === currentUserId);
    const headName = currentHead?.name || user?.name || '';

    setValue('projectHead', currentUserId, { shouldDirty: false, shouldValidate: true });
    setValue('projectHeadName', headName, { shouldDirty: false, shouldValidate: true });
  }, [isEditMode, isHeadUser, currentUserId, projectHeads, setValue, watched.projectHead, user?.name]);

  // Auto-select Project Head for manager users based on their assigned approver (head)
  useEffect(() => {
    if (isEditMode) return;
    if (!isManagerUser) return;
    if (!currentUserId) return;
    if (watched.projectHead) return;

    // Read approver info from current user (if manager has an assigned head/approver)
    const approverUserId = (user as any)?.approverUserId as string | undefined;
    const approverName = (user as any)?.approverName as string | undefined;
    if (!approverUserId) return;

    // Try to resolve approver's name from loaded projectHeads list; fallback to approverName on user
    const resolvedHead =
      projectHeads.find(h => (h._id || h.id) === approverUserId) || undefined;
    const headName = resolvedHead?.name || approverName || '';

    setValue('projectHead', approverUserId, { shouldDirty: false, shouldValidate: true });
    if (headName) {
      setValue('projectHeadName', headName, { shouldDirty: false, shouldValidate: true });
    }
  }, [
    isEditMode,
    isManagerUser,
    currentUserId,
    watched.projectHead,
    projectHeads,
    setValue,
    user
  ]);

  return (
    <div className="max-w-full mx-auto font-[family-name:var(--font-outfit)]">
      {/* Compact tab stepper + draft badge */}
      <div className="mb-5 flex flex-col gap-3">
        <div className="flex items-center gap-0 rounded-lg border border-slate-200 bg-slate-50/80 p-1">
          {TABS.map((tab, index) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const isTabValidated = isTabValid(tab.id);
            const isCompleted = index < currentTabIndex || (index === currentTabIndex && isTabValidated);
            const isPast = index < currentTabIndex;
            const hasErrors = index <= currentTabIndex && !isTabValidated;

            return (
              <div key={tab.id} className="flex items-center flex-1 min-w-0">
                <button
                  type="button"
                  onClick={() => goToTab(tab.id)}
                  className={`
                    flex items-center gap-2 px-3 py-2 rounded-md transition-all duration-200 flex-1 min-w-0
                    ${isActive
                      ? 'bg-white text-slate-900 shadow-sm border border-slate-200'
                      : isPast && isCompleted
                        ? 'text-emerald-700 hover:bg-emerald-50'
                        : hasErrors
                          ? 'text-amber-700 hover:bg-amber-50'
                          : 'text-slate-500 hover:bg-white/60'
                    }
                  `}
                >
                  <span className={`
                    flex items-center justify-center w-7 h-7 rounded-md text-xs font-semibold flex-shrink-0
                    ${isActive ? 'bg-teal-600 text-white' : isPast && isCompleted ? 'bg-emerald-100 text-emerald-700' : hasErrors ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-500'}
                  `}>
                    {isPast && isCompleted ? <Check className="w-3.5 h-3.5" /> : index + 1}
                  </span>
                  <span className="text-xs font-semibold truncate hidden sm:inline">{tab.label}</span>
                  <Icon className={`w-3.5 h-3.5 flex-shrink-0 hidden md:block ${isActive ? 'text-teal-600' : ''}`} />
                </button>
                {index < TABS.length - 1 && (
                  <div className={`w-4 h-px flex-shrink-0 ${isPast && isCompleted ? 'bg-emerald-300' : 'bg-slate-200'}`} />
                )}
              </div>
            );
          })}
        </div>
        {isEditMode && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-teal-50 border border-teal-200 rounded-lg w-fit">
            <div className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse" />
            <span className="text-xs font-medium text-teal-700">
              {createdDraftProject ? 'Draft saved' : 'Editing'}: {watched.projectId || '—'}
            </span>
          </div>
        )}
      </div>

      {/* Error Alert */}
      {apiError && (
        <div className="mb-6 p-4 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
            <X className="w-4 h-4 text-red-600" />
          </div>
          <div className="flex-1">{apiError}</div>
          <button type="button" onClick={() => setApiError(null)} className="text-red-400 hover:text-red-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">

        {/* TAB 1: Basic Project Information */}
        {activeTab === 'basic' && (
          <div className="space-y-4 animate-in fade-in duration-300">
            {/* Project Identification */}
            <div className="bg-slate-50/50 rounded-lg border border-slate-200 overflow-hidden">
              <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-teal-600" />
                <h3 className="text-sm font-semibold text-slate-800">Project Identification</h3>
                <span className="text-[10px] font-medium text-amber-600 ml-1">* Required</span>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="projectId">Project ID *</Label>
                    <Input
                      id="projectId"
                      placeholder="e.g., PRJ-001"
                      {...register('projectId', {
                        required: 'Project ID is required',
                        minLength: { value: 2, message: 'Must be at least 2 characters' },
                        maxLength: { value: 30, message: 'Must be less than 30 characters' }
                      })}
                      error={!!errors.projectId}
                      success={!!watched.projectId && !errors.projectId}
                      hint={errors.projectId?.message as string}
                      disabled={isEditMode}
                      className={isEditMode ? 'bg-gray-100 cursor-not-allowed' : ''}
                    />
                    {isEditMode && (
                      <p className="mt-1 text-xs text-gray-500">Project ID cannot be changed</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="projectName">Project Name *</Label>
                    <Input
                      id="projectName"
                      placeholder="Enter project name"
                      {...register('projectName', {
                        required: 'Project name is required',
                        minLength: { value: 3, message: 'Must be at least 3 characters' },
                        maxLength: { value: 80, message: 'Must be less than 80 characters' }
                      })}
                      error={!!errors.projectName}
                      success={!!watched.projectName && !errors.projectName}
                      hint={errors.projectName?.message as string}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Project Type & Customer */}
            <div className="bg-slate-50/50 rounded-lg border border-slate-200 overflow-hidden">
              <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2">
                <FileText className="w-4 h-4 text-teal-600" />
                <h3 className="text-sm font-semibold text-slate-800">Project Configuration</h3>
              </div>
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="projectTypeId">Project Type *</Label>
                    <Select
                      options={projectTypeOptions}
                      placeholder={loadingMasters ? 'Loading...' : projectTypeOptions.length > 0 ? 'Select project type' : 'No project types available'}
                      value={watched.projectTypeId}
                      onChange={(opt: any) => {
                        const id = opt?.value || '';
                        const label = opt?.label || '';
                        const selectedProjectType = projectTypes.find(pt => pt._id === id);
                        setValue('projectTypeId', id, { shouldDirty: true, shouldValidate: true });
                        setValue('projectType', label, { shouldDirty: true, shouldValidate: true });
                        if (selectedProjectType) {
                          setValue('projectCategory', selectedProjectType.category, { shouldDirty: true, shouldValidate: true });
                        }
                      }}
                      isLoading={loadingMasters}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    />
                    {errors.projectTypeId && (
                      <p className="mt-1 text-sm text-red-600">{errors.projectTypeId.message as string}</p>
                    )}
                  </div>
                  <div>
                    <Label>Project Category</Label>
                    <div className="h-11 px-4 border border-gray-200 rounded-lg bg-gray-50 flex items-center">
                      <span className={`text-sm font-medium ${watched.projectCategory ? 'text-gray-900' : 'text-gray-400'}`}>
                        {watched.projectCategory ? getCategoryLabel(watched.projectCategory) : 'Auto-filled from type'}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Customer *</Label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Select
                        options={customerOptions}
                        placeholder={loadingMasters || isLoadingCustomers ? 'Loading...' : 'Select customer'}
                        value={watched.customerId}
                        onChange={(opt: any) => setValue('customerId', opt?.value || '', { shouldDirty: true, shouldValidate: true })}
                        isLoading={loadingMasters || isLoadingCustomers}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsCustomerDrawerOpen(true)}
                      className="px-4 h-11 border-2 border-dashed border-gray-300 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                      disabled={loadingMasters}
                    >
                      <Plus size={18} className="text-gray-500" />
                    </Button>
                  </div>
                  {errors.customerId && (
                    <p className="mt-1 text-sm text-red-600">{errors.customerId.message as string}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Project Timeline – date inputs with strict validation (4-digit year, valid range) */}
            <div className="bg-slate-50/50 rounded-lg border border-slate-200 overflow-hidden">
              <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2">
                <FileText className="w-4 h-4 text-teal-600" />
                <h3 className="text-sm font-semibold text-slate-800">Project Timeline</h3>
                <span className="text-[10px] font-medium text-amber-600 ml-1">* Start required</span>
                <span className="text-[10px] text-slate-500 ml-1">Year: {TIMELINE_MIN_YEAR}–{TIMELINE_MAX_YEAR}</span>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="startDate">Start Date *</Label>
                    <input
                      id="startDate"
                      type="date"
                      value={watched.startDate || ''}
                      min={`${TIMELINE_MIN_YEAR}-01-01`}
                      max={`${TIMELINE_MAX_YEAR}-12-31`}
                      onChange={(e) => {
                        const v = e.target.value;
                        setValue('startDate', v, { shouldDirty: true, shouldValidate: true });
                        if (watched.endDate) validateDateRange(v, watched.endDate);
                      }}
                      className="block w-full h-11 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                      aria-invalid={!!errors.startDate}
                    />
                    {errors.startDate && (
                      <p className="mt-1 text-sm text-red-600">{errors.startDate.message as string}</p>
                    )}
                    {dateWarnings.startDatePast && !errors.startDate && (
                      <p className="mt-1 text-xs text-amber-600">Start date is in the past</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="endDate">End Date</Label>
                    <input
                      id="endDate"
                      type="date"
                      value={watched.endDate || ''}
                      min={watched.startDate || `${TIMELINE_MIN_YEAR}-01-01`}
                      max={`${TIMELINE_MAX_YEAR}-12-31`}
                      onChange={(e) => {
                        const v = e.target.value;
                        setValue('endDate', v, { shouldDirty: true, shouldValidate: true });
                        if (watched.startDate) validateDateRange(watched.startDate, v);
                      }}
                      className="block w-full h-11 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                      aria-invalid={!!errors.endDate}
                    />
                    {(errors.endDate || dateWarnings.endDateBeforeStart) && (
                      <p className="mt-1 text-sm text-red-600">
                        {(errors.endDate?.message as string) || 'End date must be after start date'}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: Team & Resources */}
        {activeTab === 'team' && (
          <div className="space-y-6 animate-in fade-in duration-300">
            {/* Project Leadership */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Users className="w-5 h-5 text-orange-600" />
                  Project Leadership
                </h3>
                <p className="text-sm text-gray-500 mt-1">Key decision makers and project oversight</p>
              </div>
              <div className="p-6 space-y-6">
                {/* Project Manager */}
                <div>
                  <Label htmlFor="managerTeamTab">Project Manager *</Label>
                  <Select
                    options={managerOptions}
                    placeholder={loadingMasters ? 'Loading...' : managerOptions.length > 0 ? 'Select manager' : 'No users available'}
                    value={watched.manager}
                    onChange={(opt: any) => {
                      const id = opt?.value || '';
                      const selectedUser = managers.find(u => (u._id || u.id) === id);
                      if (selectedUser) {
                        setValue('manager', id, { shouldDirty: true, shouldValidate: true });
                        setValue('projectManager', selectedUser.name, { shouldDirty: true, shouldValidate: true });
                      }
                    }}
                    isLoading={loadingMasters}
                    isDisabled={managerOptions.length === 0}
                  />
                  {errors.manager && (
                    <p className="mt-1 text-sm text-red-600">{errors.manager.message as string}</p>
                  )}
                  {watched.manager && (
                    <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
                      <Check className="w-4 h-4" />
                      {managers.find(u => (u._id || u.id) === watched.manager)?.name || user?.name || 'Selected'}
                    </p>
                  )}
                  <p className="mt-2 text-xs text-gray-500">Project manager is responsible for overall project execution and delivery</p>
                </div>

                {/* Project Head */}
                <div>
                  <Label htmlFor="projectHead">Project Head <span className="text-gray-400 font-normal">(Optional)</span></Label>
                  <Select
                    options={projectHeadOptions}
                    placeholder={loadingMasters ? 'Loading...' : projectHeadOptions.length > 0 ? 'Select project head' : 'No users available'}
                    value={watched.projectHead}
                    onChange={(opt: any) => {
                      if (projectHeadSelectDisabled) return;
                      const id = opt?.value || '';
                      const selectedUser = projectHeads.find(u => (u._id || u.id) === id);
                      if (selectedUser) {
                        setValue('projectHead', id, { shouldDirty: true, shouldValidate: true });
                        setValue('projectHeadName', selectedUser.name, { shouldDirty: true, shouldValidate: true });
                      }
                    }}
                    isLoading={loadingMasters}
                    isDisabled={projectHeadSelectDisabled || projectHeadOptions.length === 0}
                  />
                  {watched.projectHead && (
                    <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
                      <Check className="w-4 h-4" />
                      {projectHeads.find(u => (u._id || u.id) === watched.projectHead)?.name || user?.name || 'Selected'}
                    </p>
                  )}
                  <p className="mt-2 text-xs text-gray-500">Project head oversees the project and approves key decisions</p>
                  <input type="hidden" {...register('projectHead')} />
                  <input type="hidden" {...register('projectHeadName')} />
                </div>
              </div>
            </div>

            {/* Site Resources */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  Site Team Assignment
                  <span className="text-xs font-normal text-gray-500 ml-2">(Optional)</span>
                </h3>
                <p className="text-sm text-gray-500 mt-1">Assign field team members for equipment and task management</p>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Site Engineers */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <Label className="mb-3">Site Engineers</Label>
                    <div className="relative mb-3">
                      <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search engineers..."
                        value={engineerSearchTerm}
                        onChange={(e) => setEngineerSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                      {engineerSearchTerm && (
                        <button type="button" onClick={() => setEngineerSearchTerm('')} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          <X size={16} />
                        </button>
                      )}
                    </div>
                    <div className="max-h-52 overflow-y-auto border border-gray-200 rounded-lg bg-white">
                      {loadingMasters ? (
                        <div className="text-center py-6 text-gray-500 text-sm">Loading...</div>
                      ) : filteredSiteEngineers.length === 0 ? (
                        <div className="text-center py-6 text-gray-500 text-sm">
                          {engineerSearchTerm ? `No matches for "${engineerSearchTerm}"` : 'No engineers available'}
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {filteredSiteEngineers.map((engineer) => (
                            <label key={engineer._id || engineer.id} className="flex items-center gap-3 p-3 hover:bg-blue-50 cursor-pointer transition-colors">
                              <input
                                type="checkbox"
                                checked={watched.assignedSiteEngineers?.includes((engineer._id || engineer.id) as string) || false}
                                onChange={(e) => {
                                  const engineerId = (engineer._id || engineer.id) as string;
                                  const currentValues = watched.assignedSiteEngineers || [];
                                  const newValues = e.target.checked ? [...currentValues, engineerId] : currentValues.filter(id => id !== engineerId);
                                  setValue('assignedSiteEngineers', newValues, { shouldDirty: true, shouldValidate: true });
                                }}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 text-sm truncate">{engineer.name}</p>
                                <p className="text-xs text-gray-500 truncate">{engineer.email}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    {watched.assignedSiteEngineers && watched.assignedSiteEngineers.length > 0 && (
                      <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
                        <Check className="w-4 h-4" />
                        {watched.assignedSiteEngineers.length} engineer(s) selected
                      </p>
                    )}
                  </div>

                  {/* Site Supervisors */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <Label className="mb-3">Site Supervisors</Label>
                    <div className="relative mb-3">
                      <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search supervisors..."
                        value={supervisorSearchTerm}
                        onChange={(e) => setSupervisorSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                      {supervisorSearchTerm && (
                        <button type="button" onClick={() => setSupervisorSearchTerm('')} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          <X size={16} />
                        </button>
                      )}
                    </div>
                    <div className="max-h-52 overflow-y-auto border border-gray-200 rounded-lg bg-white">
                      {loadingMasters ? (
                        <div className="text-center py-6 text-gray-500 text-sm">Loading...</div>
                      ) : filteredSiteSupervisors.length === 0 ? (
                        <div className="text-center py-6 text-gray-500 text-sm">
                          {supervisorSearchTerm ? `No matches for "${supervisorSearchTerm}"` : 'No supervisors available'}
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {filteredSiteSupervisors.map((supervisor) => (
                            <label key={supervisor._id || supervisor.id} className="flex items-center gap-3 p-3 hover:bg-blue-50 cursor-pointer transition-colors">
                              <input
                                type="checkbox"
                                checked={watched.assignedSiteSupervisors?.includes((supervisor._id || supervisor.id) as string) || false}
                                onChange={(e) => {
                                  const supervisorId = (supervisor._id || supervisor.id) as string;
                                  const currentValues = watched.assignedSiteSupervisors || [];
                                  const newValues = e.target.checked ? [...currentValues, supervisorId] : currentValues.filter(id => id !== supervisorId);
                                  setValue('assignedSiteSupervisors', newValues, { shouldDirty: true, shouldValidate: true });
                                }}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 text-sm truncate">{supervisor.name}</p>
                                <p className="text-xs text-gray-500 truncate">{supervisor.email}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    {watched.assignedSiteSupervisors && watched.assignedSiteSupervisors.length > 0 && (
                      <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
                        <Check className="w-4 h-4" />
                        {watched.assignedSiteSupervisors.length} supervisor(s) selected
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 3: Location & Documents */}
        {activeTab === 'location' && (
          <div className="space-y-6 animate-in fade-in duration-300">
            {/* Location Selection */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-purple-50 to-violet-50 px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-purple-600" />
                  Project Location
                  <span className="text-xs font-normal text-gray-500 ml-2">(Optional)</span>
                </h3>
              </div>
              <div className="p-6 space-y-6">
                <div>
                  <Label>Select Location</Label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Select
                        options={[
                          { value: '', label: 'Select a location' },
                          ...locations.map((location) => ({
                            value: String(location._id || location.id || ''),
                            label: `${location.name}${location.code ? ` (${location.code})` : ''}`
                          }))
                        ]}
                        value={watched.locationId || ''}
                        onChange={(option) => {
                          const selectedId = option?.value || '';
                          setValue('locationId', selectedId, { shouldDirty: true, shouldValidate: true });

                          // Auto-fetch and prefill Latitude and Longitude
                          if (selectedId) {
                            const selectedLocation = locations.find(loc => String(loc._id || loc.id) === selectedId);
                            if (selectedLocation) {
                              setValue('latitude', selectedLocation.latitude || '', { shouldDirty: true, shouldValidate: true });
                              setValue('longitude', selectedLocation.longitude || '', { shouldDirty: true, shouldValidate: true });
                            }
                          } else {
                            setValue('latitude', '', { shouldDirty: true, shouldValidate: true });
                            setValue('longitude', '', { shouldDirty: true, shouldValidate: true });
                          }
                        }}
                        placeholder="Search or select location..."
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setEditingLocation(null);
                        setIsLocationFormOpen(true);
                      }}
                      className="px-4 h-11 border-2 border-dashed border-gray-300 hover:border-purple-400 hover:bg-purple-50 transition-colors flex items-center gap-2"
                    >
                      <Plus size={18} />
                      <span className="hidden sm:inline">Add New</span>
                    </Button>
                  </div>
                  {watched.locationId && (
                    <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
                      <Check className="w-4 h-4" />
                      Location selected: {locations.find(l => String(l._id || l.id) === watched.locationId)?.name || 'Unknown'}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="latitude">Latitude</Label>
                    <Input
                      id="latitude"
                      placeholder="e.g., 12.9716"
                      {...register('latitude', {
                        pattern: {
                          value: /^-?([0-8]?[0-9]|90)(\.[0-9]{1,10})?$/,
                          message: 'Invalid latitude (-90 to 90)'
                        }
                      })}
                      error={!!errors.latitude}
                      success={!!watched.latitude && !errors.latitude}
                      hint={errors.latitude?.message as string}
                    />
                  </div>
                  <div>
                    <Label htmlFor="longitude">Longitude</Label>
                    <Input
                      id="longitude"
                      placeholder="e.g., 77.5946"
                      {...register('longitude', {
                        pattern: {
                          value: /^-?((1[0-7][0-9])|([0-9]?[0-9]))(\.[0-9]{1,10})?$|^-?180(\.[0]*)?$/,
                          message: 'Invalid longitude (-180 to 180)'
                        }
                      })}
                      error={!!errors.longitude}
                      success={!!watched.longitude && !errors.longitude}
                      hint={errors.longitude?.message as string}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Documents */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 px-6 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <FileTextIcon className="w-5 h-5 text-indigo-600" />
                    Project Documents
                    <span className="text-xs font-normal text-gray-500 ml-2">(Optional)</span>
                  </h3>
                  {attachedDocuments.length > 0 && (
                    <span className="px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-sm font-medium">
                      {attachedDocuments.length} file{attachedDocuments.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
              <div className="p-6 space-y-4">
                {/* Document list – preview cards with thumbnail, preview link, delete */}
                {attachedDocuments.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                    {attachedDocuments.map((doc, index) => (
                      <div key={index} className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden group hover:shadow-md hover:border-indigo-200 transition-all">
                        {editingDocIndex === index ? (
                          <div className="p-4 space-y-3">
                            <input
                              type="text"
                              value={editDocName}
                              onChange={(e) => setEditDocName(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              placeholder="Document name"
                            />
                            <Select
                              options={[
                                { value: 'Manual', label: 'Manual' },
                                { value: 'Specification', label: 'Specification' },
                                { value: 'Certificate', label: 'Certificate' },
                                { value: 'Other', label: 'Other' },
                              ]}
                              value={{ value: editDocType, label: editDocType }}
                              onChange={(opt) => setEditDocType(opt?.value || 'Manual')}
                              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                            />
                            <div className="flex gap-2">
                              <Button type="button" size="sm" onClick={saveEditingDoc}>Save</Button>
                              <Button type="button" size="sm" variant="outline" onClick={cancelEditingDoc}>Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {/* Preview: thumbnail for images, file icon for others */}
                            <div className="relative aspect-[4/3] bg-gray-100 flex items-center justify-center overflow-hidden">
                              {looksLikeImageMediaUrl(doc.documentFile) ? (
                                <ResolvedImage mediaSrc={doc.documentFile} alt={doc.documentName} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-16 h-16 rounded-xl bg-indigo-100 flex items-center justify-center">
                                  <FileTextIcon className="w-8 h-8 text-indigo-600" />
                                </div>
                              )}
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                            </div>
                            <div className="p-3">
                              <p className="font-medium text-gray-900 text-sm truncate" title={doc.documentName}>{doc.documentName}</p>
                              <p className="text-xs text-gray-500 mt-0.5">{doc.documentType}</p>
                              <div className="flex items-center gap-2 mt-3">
                                <button
                                  type="button"
                                  onClick={() => void fileService.openMediaInNewTab(doc.documentFile)}
                                  className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-700"
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                  Preview
                                </button>
                                <button type="button" onClick={() => startEditingDoc(index, doc)} className="p-1.5 hover:bg-gray-100 rounded text-gray-500 hover:text-gray-700" title="Edit">
                                  <PencilIcon className="w-3.5 h-3.5" />
                                </button>
                                <button type="button" onClick={() => removeDoc(index)} className="p-1.5 hover:bg-red-50 rounded text-red-500 hover:text-red-600 ml-auto" title="Delete">
                                  <TrashIcon className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload area: drop or click → direct upload (no extra button) */}
                <div className="bg-gray-50 rounded-xl border border-gray-200 p-6 space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                        <UploadIcon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Add documents</h4>
                        <p className="text-xs text-gray-500">Drop files here or click to upload · PDF, DOC, DOCX, JPG, PNG (max {MAX_FILE_SIZE_MB}MB)</p>
                      </div>
                    </div>
                    <div className="sm:w-44">
                      <Label className="text-xs text-gray-500 block mb-1">Default type for new uploads</Label>
                      <Select
                        options={[
                          { value: 'Manual', label: 'Manual' },
                          { value: 'Specification', label: 'Specification' },
                          { value: 'Certificate', label: 'Certificate' },
                          { value: 'Warranty', label: 'Warranty' },
                          { value: 'Installation', label: 'Installation' },
                          { value: 'Maintenance', label: 'Maintenance' },
                          { value: 'Other', label: 'Other' },
                        ]}
                        value={{ value: newDocType, label: newDocType }}
                        onChange={(opt: { value?: string } | null) => setNewDocType((opt?.value || 'Manual') as IProjectDocument['documentType'])}
                        isDisabled={uploadingFile}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      />
                    </div>
                  </div>

                  <div
                    role="button"
                    tabIndex={0}
                    onClick={openFileInput}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFileInput(); } }}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    className={`relative rounded-xl border-2 border-dashed p-8 text-center transition-all cursor-pointer select-none min-h-[140px] flex flex-col items-center justify-center ${uploadingFile ? 'border-indigo-400 bg-indigo-50 pointer-events-none' : isDragging ? 'border-indigo-500 bg-indigo-50 scale-[1.01]' : 'border-gray-300 hover:border-indigo-400 hover:bg-white'}`}
                  >
                    {uploadingFile ? (
                      <>
                        <div className="flex items-center gap-2 text-indigo-600 mb-2">
                          <UploadIcon className="w-5 h-5 animate-pulse" />
                          <span className="font-medium">Uploading…</span>
                        </div>
                        {uploadingFileNames.length > 0 && (
                          <p className="text-sm text-gray-600 truncate max-w-full px-2">{uploadingFileNames.length} file{uploadingFileNames.length !== 1 ? 's' : ''}</p>
                        )}
                        <ProgressBar mode="indeterminate" style={{ height: '4px', width: '100%', maxWidth: '240px', marginTop: '12px' }} className="rounded-full" />
                      </>
                    ) : (
                      <>
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mb-3">
                          <UploadIcon className="w-7 h-7 text-white" />
                        </div>
                        <p className="text-gray-700 font-medium mb-1">
                          {isDragging ? 'Drop files here' : 'Drop files here or click to upload'}
                        </p>
                        <p className="text-xs text-gray-500">Files upload immediately; you can rename and set type in the card after</p>
                      </>
                    )}
                  </div>
                  {docNameError && (
                    <p className="text-sm text-red-600 flex items-center gap-1">
                      <X className="w-4 h-4 flex-shrink-0" />
                      {docNameError}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 4: Review & Save */}
        {activeTab === 'review' && (
          <div className="space-y-6 animate-in fade-in duration-300">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  Review Project Details
                </h3>
                <p className="text-sm text-gray-500 mt-1">Please review all information before {isEditMode ? 'updating' : 'creating'} the project</p>
              </div>
              <div className="p-6">
                {/* Summary Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Basic Info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-blue-600" />
                      Project Info
                    </h4>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">ID:</dt>
                        <dd className="font-medium text-gray-900">{watched.projectId || '-'}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Name:</dt>
                        <dd className="font-medium text-gray-900 truncate max-w-[150px]">{watched.projectName || '-'}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Type:</dt>
                        <dd className="font-medium text-gray-900">{watched.projectType || '-'}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Category:</dt>
                        <dd className="font-medium text-gray-900">{getCategoryLabel(watched.projectCategory) || '-'}</dd>
                      </div>
                    </dl>
                  </div>

                  {/* Customer & Manager */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4 text-orange-600" />
                      Customer & Team
                    </h4>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Customer:</dt>
                        <dd className="font-medium text-gray-900 truncate max-w-[150px]">
                          {customers.find(c => c._id === watched.customerId)?.name || '-'}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Manager:</dt>
                        <dd className="font-medium text-gray-900 truncate max-w-[150px]">
                          {managers.find(m => (m._id || m.id) === watched.manager)?.name || user?.name || '-'}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Head:</dt>
                        <dd className="font-medium text-gray-900 truncate max-w-[150px]">
                          {projectHeads.find(h => (h._id || h.id) === watched.projectHead)?.name || 'Not assigned'}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  {/* Timeline & Location */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-purple-600" />
                      Timeline & Location
                    </h4>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Start:</dt>
                        <dd className="font-medium text-gray-900">{watched.startDate || '-'}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">End:</dt>
                        <dd className="font-medium text-gray-900">{watched.endDate || 'Not set'}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Location:</dt>
                        <dd className="font-medium text-gray-900 truncate max-w-[150px]">
                          {locations.find(l => String(l._id || l.id) === watched.locationId)?.name || 'Not set'}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  {/* Resources */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4 text-cyan-600" />
                      Site Resources
                    </h4>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Engineers:</dt>
                        <dd className="font-medium text-gray-900">{watched.assignedSiteEngineers?.length || 0} assigned</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Supervisors:</dt>
                        <dd className="font-medium text-gray-900">{watched.assignedSiteSupervisors?.length || 0} assigned</dd>
                      </div>
                    </dl>
                  </div>

                  {/* Documents */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-indigo-600" />
                      Documents
                    </h4>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Attached:</dt>
                        <dd className="font-medium text-gray-900">{attachedDocuments.length} file{attachedDocuments.length !== 1 ? 's' : ''}</dd>
                      </div>
                    </dl>
                  </div>

                  {/* Validation Status */}
                  <div className={`rounded-lg p-4 ${isValid ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                    <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isValid ? 'text-green-700' : 'text-amber-700'}`}>
                      {isValid ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                      Validation Status
                    </h4>
                    <p className={`text-sm ${isValid ? 'text-green-600' : 'text-amber-600'}`}>
                      {isValid
                        ? '✓ All required fields are complete'
                        : '⚠ Some required fields are missing'
                      }
                    </p>
                    {!isValid && (
                      <button
                        type="button"
                        onClick={() => setActiveTab('basic')}
                        className="mt-2 text-sm text-amber-700 hover:text-amber-800 underline"
                      >
                        Go back to fix issues
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Hidden inputs */}
        <input type="hidden" {...register('projectType')} />

        {/* Navigation Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 -mx-6 -mb-6 px-6 py-4 mt-6 flex items-center justify-between gap-4 shadow-lg">
          <div className="flex items-center gap-2">
            {currentTabIndex > 0 && (
              <Button
                type="button"
                variant="outline"
                onClick={goToPrevTab}
                className="flex items-center gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Previous</span>
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span className="hidden sm:inline">Step {currentTabIndex + 1} of {TABS.length}</span>
          </div>

          <div className="flex items-center gap-2">
            {currentTabIndex < TABS.length - 1 ? (
              <Button
                type="button"
                variant="sorigin"
                onClick={goToNextTab}
                disabled={!canProceedToNext() || savingDraft || uploadingFile}
                className="flex items-center gap-2 min-w-[100px]"
              >
                {savingDraft || uploadingFile ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span className="hidden sm:inline">
                      {uploadingFile ? 'Uploading...' : (createdDraftProject ? 'Next' : 'Save draft & Next')}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">{activeTab === 'basic' && !createdDraftProject ? 'Save draft & Next' : 'Next'}</span>
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            ) : (
              <Button
                type="submit"
                variant="sorigin"
                disabled={isSubmitting || !isValid || uploadingFile}
                className="min-w-[140px] flex items-center gap-2"
              >
                {isSubmitting || uploadingFile ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {uploadingFile ? 'Uploading...' : (isEditMode ? 'Updating...' : 'Creating...')}
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {isEditMode ? 'Update Project' : 'Create Project'}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </form>

      {/* Customer Creation Drawer - Outside form to prevent event bubbling */}
      <RightDrawer
        open={isCustomerDrawerOpen}
        title="Create New Customer"
        onClose={() => setIsCustomerDrawerOpen(false)}
        size="lg"
      >
        <CustomerForm
          onAdd={handleCustomerCreated}
          onClose={handleCustomerFormClose}
          showSuccessAndClose={false}
        />
      </RightDrawer>

      {/* Location Creation Drawer - Outside form to prevent event bubbling */}
      <RightDrawer
        open={isLocationFormOpen}
        title={editingLocation ? "Edit Location" : "Add Location"}
        onClose={() => {
          setIsLocationFormOpen(false);
          setEditingLocation(null);
        }}
        size="50"
      >
        <LocationForm
          editingItem={editingLocation}
          onAdd={handleLocationAdd}
          onClose={() => {
            setIsLocationFormOpen(false);
            setEditingLocation(null);
          }}
        />
      </RightDrawer>
    </div>
  );
}

